/*
 * Eticheta de stare.
 *
 * Tonul se alege dupa INTELES, nu dupa numele exact al starii: api-ul spune
 * `state` in unele locuri si `status` in altele, iar cererile si taskurile n-au
 * acelasi vocabular. Ce nu se recunoaste ramane neutru si isi pastreaza textul,
 * ca o stare noua sa se vada, nu sa dispara.
 *
 * Culoarea nu e singurul semnal. Fiecare ton are si o forma proprie a punctului
 * din fata si un contrast propriu al textului: pe un ecran de banca, un daltonist
 * trebuie sa distinga "gata" de "blocat" fara sa ghiceasca.
 */

import { labelState } from '../lib/format';

export type BadgeTone = 'ready' | 'open' | 'blocked' | 'done' | 'cancelled' | 'neutral';

export function toneForState(state: string | null | undefined): BadgeTone {
  /*
   * Spatiile si cratimele se aduc la underscore inainte de comparatie. Starile
   * de task vin ca sloguri (`pending_approval`), dar starile de tichet vin ca
   * nume din fluxul tipului de tichet, scrise de om: "Pending Approval". De cand
   * aprobarea se face pe tichet, exact acelea ajung pe ecran cel mai des, iar
   * fara normalizare ar cadea toate pe `neutral` - adica gri, langa etichete
   * colorate, ca si cum ar fi stari necunoscute.
   */
  switch (String(state ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_')) {
    case 'ready':
      return 'ready';
    case 'blocked':
    case 'waiting':
    case 'pending':
    case 'pending_approval':
    case 'waiting_for_approval':
      return 'blocked';
    case 'open':
    case 'new':
    case 'in_progress':
    case 'identified':
    case 'submitted':
      return 'open';
    case 'done':
    case 'closed':
    case 'approved':
    case 'completed':
    case 'fulfilled':
    case 'resolved':
    case 'fixed':
      return 'done';
    case 'cancelled':
    case 'canceled':
    case 'rejected':
      return 'cancelled';
    default:
      return 'neutral';
  }
}

export function Badge({
  state,
  label,
  tone,
}: {
  /* `| undefined` explicit peste `?`: cu `exactOptionalPropertyTypes`, "lipseste"
     si "e undefined" sunt lucruri diferite, iar starile din api sunt optionale
     tocmai pentru ca pot lipsi. */
  state?: string | null | undefined;
  label?: string | undefined;
  tone?: BadgeTone | undefined;
}) {
  const resolved = tone ?? toneForState(state);
  return (
    <span className={`badge badge--${resolved}`}>
      <span className="badge__dot" aria-hidden="true" />
      {label ?? labelState(state)}
    </span>
  );
}
